import { inject, injectable } from "inversify";
import { IResultData } from "./../../entities/shared/result";
import TYPES from "@/ioc/identifiers";
import { DownloadFileUseCase } from "./DownloadFile";
import isValidImageUrl from "@/utils/isValidImageUrl";
import useGetSessionStorageItem from "@/use/useGetSessionStorageItem";
import {
  CACHED_BUS_IMAGE_KEY_PREFIX,
  CACHED_QNA_IMAGE_KEY_PREFIX,
  CachedImage,
} from "@/types";

export interface GetFileUrlUseCase {
  execute(
    fileUid: string,
    useCache: boolean,
    key: string
  ): Promise<IResultData<string>>;
}

@injectable()
export class GetFileUrl implements GetFileUrlUseCase {
  constructor(
    @inject(TYPES.DownloadFileUseCase) private downloadFile: DownloadFileUseCase
  ) {}

  async execute(
    fileUid: string,
    useCache: boolean,
    key: string
  ): Promise<IResultData<string>> {
    const fileUrlResult: IResultData<string> = {
      isSuccess: false,
    };

    if (!useCache)
      return this.downloadImage(fileUid, fileUrlResult, useCache, key);

    const cachedBusImage = useGetSessionStorageItem(
      `${key}${fileUid}`
    ) as CachedImage;

    if (!cachedBusImage)
      return this.downloadImage(fileUid, fileUrlResult, useCache, key);

    const isValid = await isValidImageUrl(cachedBusImage.url);

    if (isValid) {
      fileUrlResult.data = cachedBusImage.url;
      return fileUrlResult;
    } else {
      sessionStorage.removeItem(`${key}${fileUid}`);
      return this.downloadImage(fileUid, fileUrlResult, useCache, key);
    }
  }

  /** 이미지 다운로드 후 url 반환 */
  private async downloadImage(
    fileUid: string,
    fileUrlResult: IResultData<string>,
    useCache: boolean,
    key: string
  ): Promise<IResultData<string>> {
    const downloadResult = await this.downloadFile.execute(fileUid);

    if (downloadResult.data) {
      fileUrlResult.data = window.URL.createObjectURL(downloadResult.data.file);
      if (useCache) {
        this.cacheImage(`${key}${fileUid}`, fileUrlResult.data);
      }
    } else {
      fileUrlResult.resultCode = downloadResult.resultCode;
      fileUrlResult.errorMessage = downloadResult.errorMessage;
    }

    return fileUrlResult;
  }

  /** 세션스토리지에 이미지 캐시 */
  private cacheImage(key: string, url: string) {
    const item: CachedImage = {
      url,
      timestamp: new Date().getTime(),
    };

    const cachedImages = Object.keys(sessionStorage).filter((sessionKey) =>
      sessionKey.startsWith(key)
    );

    if (cachedImages.length < this.getMaxCacheItemCount(key)) {
      sessionStorage.setItem(key, JSON.stringify(item));
      return;
    }

    this.removeOldestCachedImage(cachedImages);
    sessionStorage.setItem(key, JSON.stringify(item));
  }

  /** 세션 스토리지에서 가장 오래된 항목 제거 */
  private removeOldestCachedImage(cachedImages: string[]) {
    const itemsWithTimestamp = cachedImages
      .map((key) => ({
        key,
        item: JSON.parse(sessionStorage.getItem(key) ?? "{}") as CachedImage,
      }))
      .sort((a, b) => a.item.timestamp - b.item.timestamp);

    const oldestItem = itemsWithTimestamp[0];
    sessionStorage.removeItem(oldestItem.key);
    window.URL.revokeObjectURL(oldestItem.item.url);
  }

  /** 캐시할 수 있는 최대 수 */
  private getMaxCacheItemCount(key: string): number {
    switch (key) {
      case CACHED_BUS_IMAGE_KEY_PREFIX:
        return 20;
      case CACHED_QNA_IMAGE_KEY_PREFIX:
        return 10;
      default:
        return 20;
    }
  }
}
